/**
 * The mark at the top of the till: the shop's own, if the theme bundles one, otherwise a generic
 * shopfront drawn in the shop's colour.
 *
 * There is no logo upload and nothing stored — that was the explicit call, and it is a good one.
 * A logo store means an image pipeline, a moderation question and a 404 to design around, all to
 * solve a problem the theme already solves: a shop is recognisable because its whole app is a
 * different colour, not because of a 30px glyph. So the fallback is the normal case here, and it
 * is drawn rather than fetched — it inherits `currentColor`, which the caller points at
 * `--pos-brand-base`, so Diamond's mark is blue for the same reason Diamond's rail is.
 *
 * Decorative by default: the shop's name is always rendered beside it, and a screen reader
 * announcing the name twice is worse than not announcing the glyph at all.
 */

import { shopThemeFor } from '@pos/tokens';

import { cn } from './cn.js';

type Props = {
  /** The shop's theme code. Unknown or missing lands on the default look. */
  readonly themeCode: string | null | undefined;
  readonly size?: number;
  readonly className?: string | undefined;
  /**
   * Force the generic glyph even where the theme bundles a logo. For a sign-in screen on a
   * device that has never signed in: it must not open on any shop's artwork, OriTech's included,
   * because nobody has said which shop it is yet.
   */
  readonly neutral?: boolean;
};

export function ShopMark({ themeCode, size = 30, className, neutral = false }: Props) {
  const theme = shopThemeFor(themeCode);
  const glyph = neutral ? 'storefront' : theme.code === 'diamond' ? 'rhombus' : 'storefront';

  if (!neutral && theme.logoMark !== null) {
    return (
      <img
        src={theme.logoMark}
        alt=""
        width={size}
        height={size}
        className={cn('shrink-0', className)}
      />
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0 text-brand', className)}
    >
      {glyph === 'rhombus' ? (
        <>
          {/* Diamond: a four-sided rhombus outline, at the nav icons' stroke weight, in the shop's own
              accent. Simple on purpose — a mark that reads at 26px is a shape, not a picture. */}
          <path
            d="M16 4 28 16 16 28 4 16Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M16 4v24M4 16h24" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" opacity="0.45" />
        </>
      ) : (
        <>
      {/* An awning over a doorway. Same hand-drawn stroke weight as the nav icons, so it reads as
          part of the app rather than a placeholder somebody forgot to replace. */}
      <path
        d="M5 12.5h22v13a1.5 1.5 0 0 1-1.5 1.5h-19A1.5 1.5 0 0 1 5 25.5v-13Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M4 8.2A1.2 1.2 0 0 1 5.2 7h21.6A1.2 1.2 0 0 1 28 8.2v2.1a2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0 2.2 2.2 0 0 1-4.4 0V8.2Z"
        fill="currentColor"
        fillOpacity="0.22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M13 27v-6.2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V27"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
        </>
      )}
    </svg>
  );
}
